<script setup lang="ts">
import {
  computed,
  ref,
} from 'vue';

import type {
  IBulkEmailAffiliatesBasePayload,
} from '@modules/bulk-emails/types';
import type { VFormFilterButtonProps } from '@modules/form-widgets/components/VFormFilterButton/types';
import type {
  IFormPlainDataListboxItem,
  IFormPlainDataListboxPropsChangeState,
  SelectInputType,
} from '@modules/forms/types';
import { TRANSLATION_GROUP_BULK_EMAILS } from '@modules/bulk-emails/constants';
import {
  useBulkEmailsAffiliatesQuery,
  useBulkEmailsOptionsQuery,
} from '@modules/bulk-emails/queries';
import VFormButtonsGroupWidget from '@modules/form-widgets/components/VFormButtonsGroupWidget/VFormButtonsGroupWidget.vue';
import VFormPlainDataListbox from '@modules/forms/components/inputs/VFormPlainDataListbox/VFormPlainDataListbox.vue';
import { useI18n } from '@modules/i18n/composables';
import { TRANSLATION_GROUP_INTERFACE_LANGUAGES } from '@modules/i18n/constants';
import { watchImmediate } from '@vueuse/core';

import type { BulkEmailFiltersEmits } from './types';

const emit = defineEmits<BulkEmailFiltersEmits>();

const { getTranslation } = useI18n();

const activeIndex = ref(0);
const isLanguagesTab = computed(() => activeIndex.value === 0);
const isManagersTab = computed(() => activeIndex.value === 1);
const isStatusTab = computed(() => activeIndex.value === 2);
const isSubscriptionTab = computed(() => activeIndex.value === 3);
const isAffiliatesTab = computed(() => activeIndex.value === 4);
const affiliatesPayload = ref<IBulkEmailAffiliatesBasePayload>({
  agree_receive_emails: [],
  banned: [],
  chief_ids: [],
  locales: [],
});
const checkedIds = ref<number[]>([]);

const { data: options, isLoading: isLoadingOptions } = useBulkEmailsOptionsQuery();
const { data: affiliates, isFetching: isLoadingAffiliates } = useBulkEmailsAffiliatesQuery(
  affiliatesPayload,
  checkedIds,
  isAffiliatesTab,
);

const isLoading = computed(() => (isAffiliatesTab.value ? isLoadingAffiliates.value : isLoadingOptions.value));

const languages = computed<IFormPlainDataListboxItem<string>[]>(() => (
  options.value?.locales.map((item) => ({
    id: item,
    value: getTranslation(TRANSLATION_GROUP_INTERFACE_LANGUAGES, `LANGUAGES_${item}`).value,
    check: affiliatesPayload.value.locales.includes(item),
  })) ?? []
));

const managers = computed<IFormPlainDataListboxItem<number>[]>(() => (
  options.value?.admins.map((item) => ({
    id: item.id,
    value: item.name,
    check: affiliatesPayload.value.chief_ids.includes(item.id),
  })) ?? []
));

const statuses = computed<IFormPlainDataListboxItem<boolean>[]>(() => [
  {
    id: true,
    value: getTranslation(TRANSLATION_GROUP_BULK_EMAILS, 'STATUS_BANNED').value,
    check: affiliatesPayload.value.banned.includes(true),
  },
  {
    id: false,
    value: getTranslation(TRANSLATION_GROUP_BULK_EMAILS, 'STATUS_UNBANNED').value,
    check: affiliatesPayload.value.banned.includes(false),
  },
]);

const subscriptions = computed<IFormPlainDataListboxItem<boolean>[]>(() => [
  {
    id: true,
    value: getTranslation(TRANSLATION_GROUP_BULK_EMAILS, 'SUBSCRIBED').value,
    check: affiliatesPayload.value.agree_receive_emails.includes(true),
  },
]);

const listboxData = computed<IFormPlainDataListboxItem<SelectInputType>[]>(() => {
  if (isLanguagesTab.value) {
    return languages.value;
  }

  if (isManagersTab.value) {
    return managers.value;
  }

  if (isStatusTab.value) {
    return statuses.value;
  }

  if (isSubscriptionTab.value) {
    return subscriptions.value;
  }

  if (isAffiliatesTab.value) {
    return affiliates.value ?? [];
  }

  return [];
});

function getTab(
  index: number,
  shortKey: string,
): VFormFilterButtonProps {
  return {
    type: activeIndex.value === index ? 'secondary' : 'transparent-secondary',
    label: getTranslation(TRANSLATION_GROUP_BULK_EMAILS, shortKey).value,
    isClickable: true,
    noBorder: true,
  };
}

const filterTabs = computed<VFormFilterButtonProps[]>(() => [
  getTab(0, 'TAB_LANGUAGES'),
  getTab(1, 'TAB_ADMINS'),
  getTab(2, 'TAB_STATUS'),
  getTab(3, 'TAB_SUBSCRIPTION'),
  getTab(4, 'TAB_AFFILIATES'),
]);

function onTabChange(index: number): void {
  activeIndex.value = index;
}

function emitChange(): void {
  emit('change', {
    ...affiliatesPayload.value,
    ids: checkedIds.value,
  });
}

function onChange(payload: IFormPlainDataListboxPropsChangeState): void {
  const selectedValues: SelectInputType[] = [];

  for (const item of payload.values) {
    if (item.check) {
      selectedValues.push(item.id);
    }
  }

  if (isLanguagesTab.value) {
    affiliatesPayload.value = {
      ...affiliatesPayload.value,
      locales: (selectedValues as string[]),
    };
  }

  if (isManagersTab.value) {
    affiliatesPayload.value = {
      ...affiliatesPayload.value,
      chief_ids: (selectedValues as number[]),
    };
  }

  if (isStatusTab.value) {
    affiliatesPayload.value = {
      ...affiliatesPayload.value,
      banned: (selectedValues as boolean[]),
    };
  }

  if (isSubscriptionTab.value) {
    affiliatesPayload.value = {
      ...affiliatesPayload.value,
      agree_receive_emails: (selectedValues as boolean[]),
    };
  }

  if (isAffiliatesTab.value) {
    checkedIds.value = (selectedValues as number[]);
  }

  emitChange();
}

watchImmediate(isLoadingAffiliates, (newValue) => {
  if (!newValue) {
    const selectedValues: number[] = [];

    for (const item of (affiliates.value ?? [])) {
      if (item.check) {
        selectedValues.push(item.id);
      }
    }

    checkedIds.value = selectedValues;

    emitChange();
  }
});
</script>

<template>
  <div v-data-qa-auto-id="'BulkEmailFilters'" class="inline-block w-full rounded border border-gray-200 input-max-width">
    <VFormButtonsGroupWidget
      :buttons="filterTabs"
      no-gap
      @select-item="onTabChange"
    />
    <div class="border-t border-gray-200">
      <VFormPlainDataListbox
        :data="listboxData"
        :is-loading="isLoading"
        :sort-after-change-item="isAffiliatesTab"
        hide-errors
        no-border
        @change="onChange"
      />
    </div>
  </div>
</template>
